import { Link, Navigate, Route, Routes } from 'react-router-dom';
import Home from './pages/Home/Home';
import Atlas from './pages/Atlas/Atlas';
import Applications from './pages/Applications/Applications';
import PreAlpha from './pages/PreAlpha/PreAlpha';
import PageMeta from './components/SEO/PageMeta';
import SiteFooter from './components/Footer/SiteFooter';
import SiteNavigation from './components/Navigation/SiteNavigation';
import './pages/InformationPages.css';

const pageContent = {
  privacy: {
    path: '/privacy',
    seoTitle: 'Privacy and Local File Control | Atlas',
    seoDescription: 'Learn how Atlas is designed to organize files locally, leave originals in place, limit unnecessary data collection, and keep users in control.',
    eyebrow: 'Privacy by design',
    title: 'Your files remain yours.',
    lead: 'Atlas is designed to organize files stored across your own computer, folders, and drives while keeping control of that information in your hands.',
    cards: [
      {
        title: 'Local-first operation',
        body: 'Atlas is designed to work primarily with information stored on the user’s own device. Core organization should not require uploading a person’s digital life to a remote service.'
      },
      {
        title: 'No forced reorganization',
        body: 'Atlas is intended to organize and reference existing files without silently moving, renaming, duplicating, or restructuring them.'
      },
      {
        title: 'Clear user control',
        body: 'Features that use cloud services, synchronization, or external AI providers should be explicit, understandable, and controlled by the user.'
      },
      {
        title: 'Responsible development',
        body: 'Pre-alpha builds may collect structured testing reports provided by participants. Atlas Creative Labs will not sell personal information.'
      }
    ]
  },
  about: {
    path: '/about',
    seoTitle: 'About Atlas Creative Labs | Building Atlas in Maine',
    seoDescription: 'Atlas Creative Labs is building Atlas, a centralized desktop workspace for organizing files scattered across folders and drives without moving the originals.',
    eyebrow: 'Atlas Creative Labs',
    title: 'Software should adapt to people.',
    lead: 'Atlas Creative Labs is building practical software that reduces digital friction instead of adding another layer of complexity.',
    cards: [
      {
        title: 'The mission',
        body: 'Technology should simplify people’s lives instead of making them more complicated. That principle guides the product, interface, and engineering decisions behind Atlas.'
      },
      {
        title: 'The product direction',
        body: 'Atlas brings files scattered across folders and drives into one organized workspace, helping people find and use what they already have without rebuilding their existing file system.'
      },
      {
        title: 'How it is being built',
        body: 'Development is focused on a strong Atlas foundation first. Additional applications will be introduced publicly only as their plans and development state are ready.'
      },
      {
        title: 'Built in Maine',
        body: 'Atlas Creative Labs is being developed as a long-term Maine software company with privacy, durability, and real user workflows at the center of the work.'
      }
    ]
  },
  contact: {
    path: '/contact',
    seoTitle: 'Contact Atlas Creative Labs',
    seoDescription: 'Contact Atlas Creative Labs with questions about Atlas, partnerships, business opportunities, product feedback, or pre-alpha testing.',
    eyebrow: 'Contact',
    title: 'Talk with Atlas Creative Labs.',
    lead: 'Questions, partnerships, business opportunities, and product feedback are welcome through the official public information address.',
    cards: [
      {
        title: 'Information and general inquiries',
        body: 'For questions about Atlas, Atlas Creative Labs, formal materials, grants, advisors, organizational inquiries, partnerships, or the public website, use the address below.',
        email: 'info@atlascreativelabs.com'
      },
      {
        title: 'Pre-alpha testing',
        body: 'Prospective testers should use the structured application so the right workflow, computer, availability, and testing details are included.',
        link: '/pre-alpha',
        linkLabel: 'Open the pre-alpha application'
      },
      {
        title: 'Help us respond clearly',
        body: 'Please include your name, organization when relevant, the reason for your message, and any deadline or specific action you are requesting.'
      }
    ]
  }
};

function InformationPage({ type }) {
  const page = pageContent[type];

  return (
    <>
      <PageMeta title={page.seoTitle} description={page.seoDescription} path={page.path} />
      <main className="information-page">
        <SiteNavigation />
        <section className="information-hero">
          <div className="information-shell information-hero__content">
            <p className="information-eyebrow">{page.eyebrow}</p>
            <h1>{page.title}</h1>
            <p className="information-lead">{page.lead}</p>
          </div>
        </section>

        <section className="information-section">
          <div className="information-shell information-grid">
            {page.cards.map((card, index) => (
              <article className={`information-card${index === page.cards.length - 1 && page.cards.length % 2 === 1 ? ' information-card--wide' : ''}`} key={card.title}>
                <h2>{card.title}</h2>
                <p>{card.body}</p>
                {card.email && (
                  <div className="information-contact">
                    <a href={`mailto:${card.email}`}>{card.email}</a>
                  </div>
                )}
                {card.link && (
                  <div className="information-actions">
                    <Link className="button button--secondary" to={card.link}>{card.linkLabel}</Link>
                  </div>
                )}
              </article>
            ))}

            {type === 'about' && (
              <article className="information-card information-card--wide">
                <h2>Help shape the product early.</h2>
                <p>Structured pre-alpha testing will help Atlas improve against real workflows before broader release.</p>
                <div className="information-actions">
                  <Link className="button button--primary" to="/pre-alpha">Apply for Pre-Alpha</Link>
                  <Link className="button button--secondary" to="/atlas">Explore Atlas</Link>
                </div>
              </article>
            )}
          </div>
        </section>

        <SiteFooter />
      </main>
    </>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/atlas" element={<Atlas />} />
      <Route path="/applications" element={<Applications />} />
      <Route path="/privacy" element={<InformationPage type="privacy" />} />
      <Route path="/about" element={<InformationPage type="about" />} />
      <Route path="/pre-alpha" element={<PreAlpha />} />
      <Route path="/contact" element={<InformationPage type="contact" />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
